import React, { useState, useRef } from 'react';
import { Search, MapPin, ArrowLeft, ArrowRight } from 'lucide-react';
import { StepButtons } from '../ui';
import { useDebounce } from '../../../../hooks';
import { searchAddress } from '../../../../services/addressService';

interface LocationStepProps {
  value: {
    location?: {
      postcode: string;
      city: string;
      address: string;
    };
  };
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const LocationStep: React.FC<LocationStepProps> = ({
  value,
  onChange,
  onNext,
  onBack
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{
    address: string;
    postcode: string;
    city: string;
  }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  
  const debouncedQuery = useDebounce(searchQuery, 300);

  React.useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedQuery.length >= 3) {
        const results = await searchAddress(debouncedQuery);
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery]);

  const handleSelectAddress = (suggestion: typeof suggestions[0]) => {
    onChange('location', {
      postcode: suggestion.postcode,
      city: suggestion.city,
      address: suggestion.address
    });
    setSearchQuery(suggestion.address);
    setShowSuggestions(false);
    setIsFocused(false);
  };

  const isValidLocation = value.location?.address && value.location?.city && value.location?.postcode;

  return (
    <div className="space-y-6">
      {/* Boutons de navigation compacts en haut */}
      <div className="flex justify-between items-center pb-4 border-b border-neutral-200">
        <h2 className="text-base font-semibold text-primary">Localisation du bien</h2>
        <div className="flex items-center gap-2 ml-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Précédent
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={!isValidLocation}
            className={`px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-1 ${
              !isValidLocation ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Suivant
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <div className="relative" ref={searchRef}>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            placeholder="Rechercher une adresse..."
            className="input w-full pl-10"
          />
          {!isFocused && !searchQuery && (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && isFocused && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-neutral-200">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="w-full px-4 py-2 text-left hover:bg-neutral-50 flex items-start space-x-3"
                onClick={() => handleSelectAddress(suggestion)}
                type="button"
              >
                <MapPin className="h-5 w-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="font-medium">{suggestion.address}</div>
                  <div className="text-sm text-neutral-500">
                    {suggestion.postcode} {suggestion.city}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {value.location && (
        <div className="mt-4 p-4 bg-neutral-50 rounded-lg">
          <h3 className="font-medium mb-2">Adresse sélectionnée :</h3>
          <p>{value.location.address}</p>
          <p className="text-neutral-600">
            {value.location.postcode} {value.location.city}
          </p>
        </div>
      )}

    </div>
  );
};

export default LocationStep;